import React, { useState, useMemo, useEffect, useRef } from "react";
import { authHeaders } from "../api";
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

// The publishable key comes from the API (paired with the API's secret key in
// Doppler) so the portal can never drift from the account that creates the
// PaymentIntent. The build-time VITE key is only a fallback.
let stripePromiseCache: ReturnType<typeof loadStripe> | null = null;
function getStripePromise(): ReturnType<typeof loadStripe> {
  if (!stripePromiseCache) {
    const fallback = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
    const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';
    stripePromiseCache = authHeaders()
      .then((auth) => fetch(`${API_BASE}/api/billing/config`, { headers: { ...auth } }))
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg: { publishable_key?: string } | null) => cfg?.publishable_key || fallback)
      .catch(() => fallback)
      .then((key) => loadStripe(key));
  }
  return stripePromiseCache;
}

/* ── Plan Data ── */
// Branding (operator, 2026-09-28): one ladder, OCNexus Starter / OCNexus Pro, plus
// top-up credits. Forge and Factory are capabilities, not plan names. The ids stay
// 'forge' / 'factory' until the entitlement migration (tier + capabilities) lands.
// Billing (operator, 2026-09-29): the plan price is charged at signup (no trial),
// and each paid month grants the plan's included credits, which reset monthly and
// do not roll over. Purchased top-up credits never expire.
const PLANS = [
  {
    id: 'forge', name: 'Starter', tagline: 'Fix and maintain what you have.',
    monthly: 50,
    limits: [
      { label: 'Entry', value: 'Existing repo' },
      { label: 'Delivery', value: 'Merge-gated' },
      { label: 'Support', value: 'Included' },
    ],
    features: [
      '5,000 credits every month (resets monthly)',
      'Repository health and architectural review',
      'Tested fixes opened for your approval',
      'Continuous maintenance after delivery',
    ],
  },
  {
    id: 'factory', name: 'Pro', tagline: 'Build a new SaaS end to end.', featured: true,
    monthly: 200,
    limits: [
      { label: 'Entry', value: 'Product idea' },
      { label: 'Delivery', value: 'Verified live' },
      { label: 'Starter', value: 'Included' },
    ],
    features: [
      '10,000 credits every month (resets monthly)',
      'Plan, implement, test, and deploy',
      'Verified live product outcome',
      'Graduates into Starter maintenance',
    ],
  },
] as const;

/* ── Inline styles (matching reference design) ── */
const ps = {
  ink:       '#0A0C12',
  ink2:      '#10131D',
  ink3:      '#171B28',
  ink4:      '#1D2235',
  border:    'rgba(255,255,255,0.10)',
  borderHi:  'rgba(255,255,255,0.18)',
  text:      '#FFFFFF',
  textDim:   '#C8D0E8',
  textMute:  '#8A95B8',
  orange:    '#00E5FF',
  orangeHi:  '#4DE5FF',
  orangeDim: 'rgba(0,229,255,0.12)',
  orangeGlow:'rgba(0,229,255,0.30)',
  green:     '#22C55E',
  greenDim:  'rgba(34,197,94,0.15)',
};

/** Amount due today in dollars, after an applied subscription discount. */
export function dueTodayUsd(price: number, discount: { percent_off: number | null; amount_off: number | null } | null): number {
  if (!discount) return price;
  if (discount.percent_off != null) return Math.max(0, Math.round(price * (100 - discount.percent_off)) / 100);
  if (discount.amount_off != null) return Math.max(0, price - discount.amount_off / 100);
  return price;
}

function FeatureCheck() {
  return (
    <span style={{
      width: 16, height: 16, borderRadius: '50%', background: ps.greenDim,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <svg width="9" height="9" viewBox="0 0 10 10" fill="none" strokeWidth="2">
        <polyline points="1.5,5 4,7.5 8.5,2.5" stroke={ps.green} />
      </svg>
    </span>
  );
}

/* ── Checkout Form (inside Elements provider) ── */
function CheckoutForm({ onSuccess, onBack, planName, price, billing, error: externalError }: {
  onSuccess: () => void;
  onBack: () => void;
  planName: string;
  price: number;
  billing: 'monthly';
  error: string | null;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !elementReady) return;

    setProcessing(true);
    setPayError(null);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/?page=account&checkout=success`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setPayError(error.message || 'Payment failed');
        setProcessing(false);
      } else {
        onSuccess();
      }
    } catch (err: any) {
      setPayError(err?.message || 'Payment could not be started. Refresh and try again.');
      setProcessing(false);
    }
  };

  const displayError = payError || externalError;

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 24 }}>
        <PaymentElement
          options={{ layout: 'tabs' }}
          onReady={() => setElementReady(true)}
          onLoadError={(ev: any) => {
            setElementReady(false);
            setPayError(ev?.error?.message || 'Payment form failed to load. Refresh and try again.');
          }}
        />
      </div>

      {displayError && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', fontSize: 12,
          color: '#ef4444', background: 'rgba(239,68,68,0.08)', borderRadius: 4,
          border: '1px solid rgba(239,68,68,0.3)',
        }}>
          {displayError}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || !elementReady || processing}
        style={{
          width: '100%', padding: 16, borderRadius: 4, border: 'none',
          background: ps.orange, color: 'white',
          fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 600,
          cursor: processing ? 'wait' : 'pointer', transition: 'all 0.2s',
          letterSpacing: '0.02em',
          boxShadow: `0 4px 20px ${ps.orangeGlow}`,
          opacity: processing ? 0.7 : 1,
        }}
      >
        {processing ? 'Processing...' : `Subscribe to ${planName} — $${price}/${billing === 'monthly' ? 'mo' : billing}`}
      </button>

      <button
        type="button"
        onClick={onBack}
        style={{
          width: '100%', padding: 12, marginTop: 12, borderRadius: 4,
          border: `1px solid ${ps.border}`, background: 'transparent',
          color: ps.textMute, fontFamily: "var(--font-mono)", fontSize: 12,
          cursor: 'pointer',
        }}
      >
        ← Change plan
      </button>
    </form>
  );
}

/* ── Shared components ── */
const Background = () => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
    <div style={{
      position: 'absolute', inset: 0,
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
    }} />
    <div style={{
      position: 'absolute', width: 800, height: 400, top: -100, left: '50%',
      transform: 'translateX(-50%)',
      background: 'radial-gradient(ellipse, rgba(0,229,255,0.08) 0%, transparent 70%)',
    }} />
  </div>
);

const Logo = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
    <div style={{
      width: 34, height: 34, background: ps.orange, borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 800, color: 'white',
    }}>OC</div>
    <span style={{ fontFamily: "var(--font-sans)", fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>
      OCNexus
    </span>
  </div>
);

/* ── Main PlanSelector ── */
export function PlanSelector({ user, subscriber, signOut }: {
  user: { email?: string | null; id?: string };
  subscriber: { id: string };
  signOut: () => Promise<void>;
}) {
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const urlPlan = urlParams.get('plan');
  const fromSignup = urlParams.get('source') === 'signup';
  const [billing, setBilling] = useState<'monthly'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState(urlPlan || 'factory');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pendingCheckout, setPendingCheckout] = useState<{ url: string; planName: string } | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checkoutPlan, setCheckoutPlan] = useState<typeof PLANS[number] | null>(null);

  // ── Discount code (ocn-S420-P0) ──
  // Collapsed by default (link-reveal); sent through to the API on
  // continue and shown back on the order summary once the API confirms
  // it applied.
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    promotion_code: string;
    name: string | null;
    percent_off: number | null;
    amount_off: number | null;
    currency: string | null;
  } | null>(null);

  const handleContinue = async (planId: string) => {
    setLoading(true);
    setError(null);
    setPendingCheckout(null);
    setSelectedPlan(planId);
    try {
      const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';
      const auth = await authHeaders();
      const trimmedCode = discountCode.trim();
      const res = await fetch(`${API_BASE}/api/billing/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({
          plan: planId,
          billing_period: billing,
          user_id: subscriber.id,
          email: user.email,
          ...(trimmedCode ? { promotion_code: trimmedCode } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'checkout_already_pending') {
          const existing = data.pending_checkout;
          const existingPlan = PLANS.find((candidate: { id: string }) => candidate.id === existing?.plan);
          if (existingPlan && existing?.billing_period === 'monthly' && typeof existing.url === 'string') {
            const url = new URL(existing.url);
            if (url.protocol === 'https:' && url.hostname === 'invoice.stripe.com' && !url.username && !url.password) {
              setPendingCheckout({ url: url.href, planName: existingPlan.name });
              throw new Error(`An unpaid ${existingPlan.name} monthly checkout already exists. Its plan or discount differs from your selection. Review the existing amount and discount on Stripe before paying.`);
            }
          }
          throw new Error('A previous checkout is still pending. Contact support to review it.');
        }
        if (data.error === 'invalid_promotion_code') {
          throw new Error('That discount code is invalid or expired.');
        }
        throw new Error(data.error || 'Failed to create subscription');
      }
      if (!data.client_secret) throw new Error('No client secret returned');

      setClientSecret(data.client_secret);
      setCheckoutPlan(PLANS.find((p: { id: string }) => p.id === planId) || null);
      setAppliedDiscount(data.discount ?? null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setClientSecret(null);
    setCheckoutPlan(null);
    setAppliedDiscount(null);
    setError(null);
  };

  // Stay in the portal after payment: Account & billing shows the new plan.
  const handleSuccess = () => {
    window.location.href = `${window.location.origin}/?page=account&checkout=success`;
  };

  const autoTriggered = useRef(false);
  useEffect(() => {
    if (fromSignup && urlPlan && PLANS.some((p: { id: string }) => p.id === urlPlan) && !autoTriggered.current) {
      autoTriggered.current = true;
      void handleContinue(urlPlan);
    }
  }, [fromSignup, urlPlan]);

  // ═══════════════════════════════════════════════════════════
  // STEP 2: Embedded Payment Element with plan summary
  // ═══════════════════════════════════════════════════════════
  if (clientSecret && checkoutPlan) {
    const price = checkoutPlan.monthly;
    const dueToday = dueTodayUsd(price, appliedDiscount);

    return (
      <div style={{
        minHeight: '100vh', background: ps.ink, color: ps.text,
        fontFamily: "var(--font-mono)",
        position: 'relative', overflow: 'hidden',
      }}>
        <Background />
        <div style={{
          position: 'relative', zIndex: 1, minHeight: '100vh',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '56px 24px 80px',
        }}>
          <Logo />

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32,
            width: '100%', maxWidth: 820,
          }}>
            {/* LEFT: Order Summary */}
            <div style={{
              background: ps.ink2, border: `1px solid ${ps.border}`, borderRadius: 6,
              padding: '32px 28px',
            }}>
              <h2 style={{
                fontFamily: "var(--font-sans)", fontSize: 22, fontWeight: 700,
                marginBottom: 24, letterSpacing: '-0.02em',
              }}>
                Order summary
              </h2>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ fontFamily: "var(--font-sans)", fontSize: 18, fontWeight: 700 }}>
                  {checkoutPlan.name}
                </span>
                <span style={{
                  fontSize: 10, padding: '3px 10px', borderRadius: 100,
                  background: ps.orangeDim, color: ps.orangeHi,
                  fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' as const,
                }}>
                  {billing}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
                <span style={{
                  fontFamily: "var(--font-sans)", fontSize: 36, fontWeight: 800,
                  letterSpacing: '-0.03em', lineHeight: 1,
                }}>
                  ${price}
                </span>
                <span style={{ fontSize: 13, color: ps.textMute }}>/mo</span>
              </div>
              {appliedDiscount && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 100, marginBottom: 16,
                  background: ps.greenDim, color: ps.green, fontSize: 11, fontWeight: 500,
                }}>
                  {appliedDiscount.percent_off != null
                    ? `${appliedDiscount.percent_off}% off`
                    : appliedDiscount.amount_off != null
                      ? `$${(appliedDiscount.amount_off / 100).toFixed(2)} off`
                      : 'Discount applied'}
                  {appliedDiscount.name ? ` — ${appliedDiscount.name}` : ''}
                </div>
              )}

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0,
                marginBottom: 20, padding: '14px 0',
                borderTop: `1px solid ${ps.border}`, borderBottom: `1px solid ${ps.border}`,
              }}>
                {checkoutPlan.limits.map((lim: { label: string; value: string }, i: number) => (
                  <div key={i} style={{ textAlign: 'center' as const, padding: '4px 0' }}>
                    <div style={{
                      fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 700,
                      color: ps.orange, lineHeight: 1.2,
                    }}>
                      {lim.value}
                    </div>
                    <div style={{ fontSize: 10, color: ps.textMute, letterSpacing: '0.04em', textTransform: 'uppercase' as const, marginTop: 2 }}>
                      {lim.label}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {checkoutPlan.features.map((f: string, i: number) => {
                  const isHeader = f.endsWith(':');
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      fontSize: 12, color: isHeader ? ps.textMute : ps.textDim,
                      fontWeight: isHeader ? 500 : 400,
                      fontStyle: isHeader ? 'italic' : 'normal',
                    }}>
                      {!isHeader && <FeatureCheck />}
                      {f}
                    </div>
                  );
                })}
              </div>

              <div style={{ borderTop: `1px solid ${ps.border}`, paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: ps.textDim }}>
                  <span>{checkoutPlan.name} plan ({billing})</span>
                  <span style={{ color: ps.text, fontWeight: 500 }}>${price.toFixed(2)}</span>
                </div>
                {appliedDiscount && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: ps.green }}>
                    <span>Discount ({appliedDiscount.promotion_code})</span>
                    <span>
                      {appliedDiscount.percent_off != null
                        ? `−${appliedDiscount.percent_off}%`
                        : appliedDiscount.amount_off != null
                          ? `−$${(appliedDiscount.amount_off / 100).toFixed(2)}`
                          : 'applied'}
                    </span>
                  </div>
                )}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', marginTop: 12, paddingTop: 12,
                  borderTop: `1px solid ${ps.border}`, fontSize: 15,
                }}>
                  <span style={{ fontWeight: 600 }}>Due today</span>
                  <span style={{ fontFamily: "var(--font-sans)", fontWeight: 800, fontSize: 22, color: ps.orange }}>${dueToday.toFixed(2)}</span>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: ps.textMute }}>
                  Renews monthly. Cancel anytime from Account &amp; billing.
                </div>
              </div>
            </div>

            {/* RIGHT: Payment Element */}
            <div style={{
              background: ps.ink2, border: `1px solid ${ps.border}`, borderRadius: 6,
              padding: '32px 28px',
            }}>
              <h2 style={{
                fontFamily: "var(--font-sans)", fontSize: 22, fontWeight: 700,
                marginBottom: 8, letterSpacing: '-0.02em',
              }}>
                Payment details
              </h2>
              <p style={{ fontSize: 12, color: ps.textMute, marginBottom: 24 }}>
                Secure payment powered by Stripe
              </p>

              <Elements
                stripe={getStripePromise()}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'night',
                    variables: {
                      colorPrimary: ps.orange,
                      colorBackground: ps.ink3,
                      colorText: ps.text,
                      colorTextSecondary: ps.textMute,
                      colorDanger: '#ef4444',
                      fontFamily: "var(--font-mono)",
                      borderRadius: '4px',
                      spacingUnit: '4px',
                    },
                    rules: {
                      '.Input': {
                        border: `1px solid ${ps.borderHi}`,
                        backgroundColor: ps.ink4,
                        color: ps.text,
                      },
                      '.Input:focus': {
                        border: `1px solid ${ps.orange}`,
                        boxShadow: `0 0 0 1px ${ps.orange}`,
                      },
                      '.Label': {
                        color: ps.textDim,
                        fontSize: '12px',
                      },
                      '.Tab': {
                        border: `1px solid ${ps.border}`,
                        backgroundColor: ps.ink3,
                      },
                      '.Tab--selected': {
                        border: `1px solid ${ps.orange}`,
                        backgroundColor: ps.ink4,
                      },
                    },
                  },
                }}
              >
                <CheckoutForm
                  onSuccess={handleSuccess}
                  onBack={handleBack}
                  planName={checkoutPlan.name}
                  price={price}
                  billing={billing}
                  error={error}
                />
              </Elements>

              <div style={{
                marginTop: 24, padding: '14px 16px', background: ps.ink3,
                border: `1px solid ${ps.border}`, borderRadius: 4,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>&#x1f512;</span>
                <span style={{ fontSize: 11, color: ps.textMute, lineHeight: 1.5 }}>
                  <strong style={{ color: ps.textDim, fontWeight: 500 }}>No lock-in.</strong>{' '}
                  Charged today, then monthly. Cancel anytime from Account &amp; billing.
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={signOut}
            style={{
              fontSize: 12, color: ps.textMute, background: 'none', marginTop: 32,
              border: 'none', cursor: 'pointer',
              fontFamily: "var(--font-mono)", transition: 'color 0.15s',
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // STEP 1: Plan selection (existing cards UI)
  // ═══════════════════════════════════════════════════════════
  return (
    <div style={{
      minHeight: '100vh', background: ps.ink, color: ps.text,
      fontFamily: "var(--font-mono)",
      position: 'relative', overflow: 'hidden',
    }}>
      <Background />

      <div style={{
        position: 'relative', zIndex: 1, minHeight: '100vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '56px 24px 80px',
      }}>
        <Logo />

        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 16px', background: ps.orangeDim,
            border: '1px solid rgba(0,229,255,0.3)', borderRadius: 100,
            fontSize: 11, fontWeight: 500, letterSpacing: '0.08em',
            color: ps.orangeHi, textTransform: 'uppercase' as const, marginBottom: 20,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: ps.orange,
              boxShadow: `0 0 8px ${ps.orange}`,
            }} />
            Monthly plans — cancel anytime
          </div>

          <h1 style={{
            fontFamily: "var(--font-sans)", fontSize: 'clamp(32px, 4vw, 48px)',
            fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05, marginBottom: 16,
          }}>
            Choose your plan.
          </h1>

          <p style={{
            fontSize: 14, color: ps.textDim, lineHeight: 1.6,
            maxWidth: 440, margin: '0 auto',
          }}>
            Your plan is charged today and renews monthly. Your plan's included credits
            <strong style={{ color: ps.text, fontWeight: 500 }}> reset every month</strong> and unused included credits do not roll over.
            Need more? Top up anytime; top-up credits never expire.
          </p>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', background: ps.ink3,
          border: `1px solid ${ps.border}`, borderRadius: 100, padding: 4, marginBottom: 40,
        }}>
          {(['monthly'] as const).map((b: 'monthly') => (
            <button
              key={b}
              onClick={() => setBilling(b)}
              style={{
                padding: '8px 24px', borderRadius: 100, border: billing === b ? `1px solid ${ps.borderHi}` : 'none',
                background: billing === b ? ps.ink4 : 'none',
                color: billing === b ? ps.text : ps.textMute,
                fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 500,
                cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' as const,
              }}
            >
              Monthly
            </button>
          ))}
        </div>

        {/* ── Discount code (ocn-S420-P0) — collapsed/link-reveal by default ── */}
        <div style={{ marginBottom: 24, textAlign: 'center' as const }}>
          {!showDiscountInput ? (
            <button
              onClick={() => setShowDiscountInput(true)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: "var(--font-mono)", fontSize: 12, color: ps.textMute,
                textDecoration: 'underline',
              }}
            >
              Have a discount code?
            </button>
          ) : (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text"
                value={discountCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDiscountCode(e.target.value.toUpperCase())}
                placeholder="Discount code"
                style={{
                  padding: '8px 12px', borderRadius: 4, border: `1px solid ${ps.borderHi}`,
                  background: ps.ink3, color: ps.text, fontFamily: "var(--font-mono)",
                  fontSize: 12, letterSpacing: '0.04em', width: 160,
                }}
              />
              <button
                onClick={() => { setShowDiscountInput(false); setDiscountCode(''); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: "var(--font-mono)", fontSize: 11, color: ps.textMute,
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16,
          width: '100%', maxWidth: 760, marginBottom: 40,
        }}>
          {PLANS.map((plan) => {
            const isDisabled = false;
            const isSelected = !isDisabled && selectedPlan === plan.id;
            const isFeatured = 'featured' in plan && plan.featured;
            const price = plan.monthly;

            return (
              <div
                key={plan.id}
                onClick={() => !isDisabled && setSelectedPlan(plan.id)}
                style={{
                  background: isSelected
                    ? `linear-gradient(135deg, rgba(0,229,255,0.06) 0%, ${ps.ink2} 60%)`
                    : ps.ink2,
                  border: `1px solid ${isSelected ? ps.orange : ps.border}`,
                  borderRadius: 4, padding: '32px 28px', position: 'relative',
                  cursor: isDisabled ? 'default' : 'pointer', display: 'flex', flexDirection: 'column' as const,
                  opacity: isDisabled ? 0.45 : 1,
                  transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
                  boxShadow: isSelected ? `0 0 40px rgba(0,229,255,0.12), 0 8px 32px rgba(0,0,0,0.4)` : 'none',
                }}
              >
                {isFeatured && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    background: ps.orange, color: 'white', fontSize: 10, fontWeight: 500,
                    letterSpacing: '0.12em', textTransform: 'uppercase' as const,
                    padding: '4px 14px', borderRadius: 100, whiteSpace: 'nowrap' as const,
                  }}>
                    Includes Forge
                  </div>
                )}

                <div style={{ fontFamily: "var(--font-sans)", fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
                  {plan.name}
                </div>
                <div style={{ fontSize: 12, color: ps.textMute, marginBottom: 24 }}>
                  {plan.tagline}
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
                  <span style={{
                    fontFamily: "var(--font-sans)", fontSize: 40, fontWeight: 800,
                    letterSpacing: '-0.03em', lineHeight: 1,
                  }}>
                    ${price}
                  </span>
                  <span style={{ fontSize: 13, color: ps.textMute }}>/mo</span>
                </div>

                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0,
                  marginBottom: 20, padding: '12px 0',
                  borderTop: `1px solid ${ps.border}`, borderBottom: `1px solid ${ps.border}`,
                }}>
                  {plan.limits.map((lim: { label: string; value: string }, i: number) => (
                    <div key={i} style={{ textAlign: 'center' as const, padding: '4px 0' }}>
                      <div style={{
                        fontFamily: "var(--font-sans)", fontSize: 16, fontWeight: 700,
                        color: isSelected ? ps.orange : ps.text, lineHeight: 1.2,
                      }}>
                        {lim.value}
                      </div>
                      <div style={{ fontSize: 10, color: ps.textMute, letterSpacing: '0.04em', textTransform: 'uppercase' as const, marginTop: 2 }}>
                        {lim.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8, marginBottom: 28, flex: 1 }}>
                  {plan.features.map((f: string, i: number) => {
                    const isHeader = f.endsWith(':');
                    return (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        fontSize: 12, color: isHeader ? ps.textMute : ps.textDim,
                        fontWeight: isHeader ? 500 : 400,
                        fontStyle: isHeader ? 'italic' : 'normal',
                      }}>
                        {!isHeader && <FeatureCheck />}
                        {f}
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); if (!isDisabled) void handleContinue(plan.id); }}
                  disabled={loading || isDisabled}
                  style={{
                    width: '100%', padding: 14, borderRadius: 3,
                    border: isSelected ? `1px solid ${ps.orange}` : `1px solid ${ps.borderHi}`,
                    background: isDisabled ? ps.ink3 : isSelected ? ps.orange : ps.ink3,
                    color: isDisabled ? ps.textMute : isSelected ? 'white' : ps.textDim,
                    fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 500,
                    cursor: isDisabled ? 'default' : loading ? 'wait' : 'pointer', transition: 'all 0.2s',
                    letterSpacing: '0.02em',
                    boxShadow: isSelected ? `0 4px 20px ${ps.orangeGlow}` : 'none',
                    opacity: isDisabled ? 0.6 : loading ? 0.7 : 1,
                  }}
                >
                  {loading && selectedPlan === plan.id ? 'Loading...' : `Choose ${plan.name} — $${price}/mo`}
                </button>
              </div>
            );
          })}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 20,
          padding: '20px 32px', background: ps.ink2,
          border: `1px solid ${ps.border}`, borderRadius: 4,
          maxWidth: 1000, width: '100%', marginBottom: 24,
        }}>
          <div style={{ fontSize: 24, flexShrink: 0 }}>&#x1f512;</div>
          <div style={{ fontSize: 13, color: ps.textDim, lineHeight: 1.5 }}>
            <strong style={{ color: ps.text, fontWeight: 500 }}>No lock-in.</strong>{' '}
            Pay monthly and cancel anytime from Account &amp; billing.
            Included credits reset monthly; top-up credits never expire.
          </div>
          <div style={{ display: 'flex', gap: 32, marginLeft: 'auto', flexShrink: 0 }}>
            {[
              { val: 'Monthly', lbl: 'Billing' },
              { val: 'Monthly', lbl: 'Credit reset' },
              { val: '0', lbl: 'Lock-in' },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: 'center' as const }}>
                <span style={{
                  fontFamily: "var(--font-sans)", fontSize: 20, fontWeight: 700,
                  color: ps.orange, display: 'block',
                }}>{item.val}</span>
                <span style={{ fontSize: 10, color: ps.textMute, letterSpacing: '0.06em', textTransform: 'uppercase' as const }}>
                  {item.lbl}
                </span>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div style={{
            marginBottom: 16, padding: '8px 16px', fontSize: 12,
            color: '#ef4444', background: ps.ink2, borderRadius: 4,
            border: '1px solid #ef4444',
          }}>
            {error}
            {pendingCheckout && (
              <div style={{ marginTop: 12 }}>
                <a href={pendingCheckout.url} target="_blank" rel="noopener noreferrer" style={{ color: ps.orangeHi }}>
                  Review existing {pendingCheckout.planName} monthly checkout on Stripe
                </a>
              </div>
            )}
          </div>
        )}

        <button
          onClick={signOut}
          style={{
            fontSize: 12, color: ps.textMute, background: 'none',
            border: 'none', cursor: 'pointer',
            fontFamily: "var(--font-mono)", transition: 'color 0.15s',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
